<template>
    <div>
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Debtor Receipt</h5>
          <p class="page-sub">
            <span class="batch-pill">{{ sales?.batch_code }}</span>
          </p>
        </div>
        <div class="page-actions">
          <router-link :to="$url('/user-debtor/' + debtor?.customer?.id)" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </router-link>
          <PrintButtonHeader
            :title="'Invoice-' + sales?.batch_code"
            :urlA4="'/debtor-receipt/'"
            :url58mm="'/print58mm-debtor-receipt/'"
            :url80mm="'/print80mm-debtor-receipt/'"
            :currentUrl="'View debtor Receipt'"
            :data="debtor_debts"
            :type="'Debtor Receipt'"
            :loading="loading"
          />
        </div>
      </div>
  
      <!-- Skeleton -->
      <div v-if="loading" class="form-card mb-4">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 4" :key="n"></div>
        </div>
        <div class="skeleton skeleton--table mt-4"></div>
      </div>
  
      <div v-else class="page-grid">
  
        <!-- LEFT: Invoice -->
        <div class="invoice-panel" id="printMe">
  
          <!-- Header -->
          <div class="invoice-header">
            <div class="invoice-title-block">
              <h1 class="invoice-title">Invoice</h1>
              <span class="invoice-batch">{{ sales?.batch_code }}</span>
            </div>
            <div class="invoice-logo">
              <img :src="businessLogoSrc" alt="Logo" />
            </div>
          </div>
  
          <div class="invoice-divider"></div>
  
          <!-- Parties -->
          <div class="invoice-parties">
            <div class="party-block">
              <p class="party-label">Bill To</p>
              <p class="party-name">{{ debtor_debts?.debtor?.customer?.customer_name }}</p>
              <p class="party-detail" v-if="debtor_debts?.debtor?.customer?.address">{{ debtor_debts.debtor.customer.address }}</p>
              <p class="party-detail" v-if="debtor_debts?.debtor?.customer?.email">{{ debtor_debts.debtor.customer.email }}</p>
            </div>
            <div class="party-block party-block--right">
              <p class="party-label">From</p>
              <p class="party-name">{{ loggedInUser?.business?.company_name }}</p>
              <p class="party-detail" v-if="loggedInUser?.business?.address">{{ loggedInUser.business.address }}</p>
              <p class="party-detail" v-if="loggedInUser?.business?.phone">{{ loggedInUser.business.phone }}</p>
            </div>
          </div>
  
          <div class="invoice-divider invoice-divider--dashed"></div>
  
          <!-- Sale Meta -->
          <div class="sale-meta-grid">
            <div class="sale-meta-item">
              <span class="sale-meta-label">Sale Date</span>
              <span class="sale-meta-value">{{ dateTime(sales?.date) }}</span>
            </div>
            <div class="sale-meta-item">
              <span class="sale-meta-label">Sale No.</span>
              <span class="sale-meta-value mono">{{ sales?.batch_code }}</span>
            </div>
            <div class="sale-meta-item">
              <span class="sale-meta-label">Store</span>
              <span class="sale-meta-value">{{ debtor?.store?.store_name }}</span>
            </div>
            <div class="sale-meta-item">
              <span class="sale-meta-label">Sold By</span>
              <span class="sale-meta-value">{{ debtor?.user?.fullname }}</span>
            </div>
          </div>
  
          <div class="invoice-divider invoice-divider--dashed"></div>
  
          <!-- Items Table -->
          <div class="table-scroll">
            <table class="invoice-table">
              <thead>
                <tr>
                  <th style="width:28%">Product</th>
                  <th>Variant</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Price ({{ company?.currency?.symbol }})</th>
                  <th class="text-right">Discount</th>
                  <th class="text-right">Tax</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in sales_array" :key="p.product_name">
                  <td class="fw-bold">{{ p?.product_name }}</td>
                  <td class="text-muted">{{ p?.variant_name || '—' }}</td>
                  <td class="text-right">{{ p?.quantity }}</td>
                  <td class="text-right text-muted">{{ $filters.formatMoney(p?.selling_price) }}</td>
                  <td class="text-right text-muted">{{ p?.discount ? $filters.formatMoney(p.discount) : 'N/A' }}</td>
                  <td class="text-right text-muted">{{ p?.tax ? $filters.formatMoney(p.tax) : 'N/A' }}</td>
                  <td class="text-right fw-bold">{{ $filters.formatMoney(p?.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Totals -->
          <div class="totals-block">
            <div class="totals-row">
              <span class="totals-label">Subtotal</span>
              <span class="totals-value">{{ $filters.formatMoney(subTotal, company?.currency?.symbol) }}</span>
            </div>
            <div class="totals-row">
              <span class="totals-label">Tax</span>
              <span class="totals-value">{{ $filters.formatMoney(taxTotal, company?.currency?.symbol) }}</span>
            </div>
            <div class="totals-row">
              <span class="totals-label">Discount</span>
              <span class="totals-value totals-value--discount">– {{ $filters.formatMoney(discountTotal, company?.currency?.symbol) }}</span>
            </div>
            <div class="totals-divider"></div>
            <div class="totals-row totals-row--grand">
              <span class="totals-label">Total</span>
              <span class="totals-value totals-value--grand">{{ $filters.formatMoney(grandTotal, company?.currency?.symbol) }}</span>
            </div>
          </div>
  
          <div class="invoice-divider"></div>
  
          <!-- Payment Info -->
          <div class="payment-block">
            <p class="payment-title">Payment Information</p>
            <div class="payment-grid">
              <div class="payment-row">
                <span class="payment-label">Payment Type</span>
                <span class="payment-badge" :class="paymentClass(sales?.payment_type)">
                  {{ sales?.payment_type == 'part' ? 'Part Payment' : sales?.payment_type }}
                </span>
              </div>
              <div class="payment-row">
                <span class="payment-label">Amount Paid</span>
                <span class="payment-value">{{ $filters.formatMoney(debtor_debts?.amount_paid, company?.currency?.symbol) }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">Balance</span>
                <span class="payment-value" :class="parseFloat(debtor_debts?.amount_owned) > 0 ? 'text-neg' : ''">
                  {{ $filters.formatMoney(debtor_debts?.amount_owned ?? 0, company?.currency?.symbol) }}
                </span>
              </div>
            </div>
          </div>
  
        </div>
  
        <!-- RIGHT: Record Receipt -->
        <div class="receipt-panel">
          <div class="form-card">
            <div class="section-header">
              <div class="section-icon section-icon--blue">
                <font-awesome-icon icon="hand-holding-usd" />
              </div>
              <div>
                <p class="section-title">Record Receipt</p>
                <p class="section-sub">Record a payment from this customer</p>
              </div>
            </div>
  
            <!-- Outstanding balance chip -->
            <div class="outstanding-chip">
              <span class="outstanding-label">Outstanding Balance</span>
              <span class="outstanding-value">{{ $filters.formatMoney(def_balance, company?.currency?.symbol) }}</span>
            </div>
  
            <div class="field-group mt-3">
              <label class="field-label">Payment Type</label>
              <select class="field-input field-select" v-model="payment_type" :disabled="permission.create_record_receipts != 1">
                <option value="" disabled selected>Select payment type</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
              </select>
            </div>
  
            <div class="field-group mt-3">
              <label class="field-label">Payment Amount</label>
              <input
                type="text"
                class="field-input"
                v-model="amount"
                @keyup="handleAmount"
                :disabled="permission.create_record_receipts != 1"
                placeholder="0.00"
                onkeypress="return /^[0-9.]+$/i.test(event.key)"
              />
            </div>
  
            <div class="field-group mt-3">
              <label class="field-label">Remaining Balance</label>
              <input disabled type="number" class="field-input field-input--readonly" v-model="balance" />
            </div>
  
            <button
              class="btn-primary-action w-full mt-4"
              @click.prevent="makePayment"
              :disabled="crudLoading || permission.create_record_receipts != 1"
            >
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="check" class="me-2" />
              {{ crudLoading ? 'Processing...' : 'Record Receipt' }}
            </button>
          </div>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import hostName from '../../../main';
  
  export default {
    mixins: [reusableMixin],
  
    data() {
      return {
        toast:        useToast(),
        store:        useStore(),
        url:          hostName,
        business:     {},
        debtor_debts: null,
        debtor:       [],
        sales_items:  [],
        sales:        null,
        sales_array:  [],
        payment_type: null,
        amount:       null,
        balance:      '0.00',
        def_balance:  '0.00',
        crudLoading:  false,
        loading:      true,
      };
    },
  
    computed: {
      loggedInUser()    { return this.store.state.getLoggedInUser; },
      businessLogoSrc() {
        return this.business.logo
          ? this.url.hostName + this.business.logo
          : require('../../private-assets/images/logo.png');
      },
      grandTotal() {
        return this.sales_array.reduce((a, b) =>
          (parseFloat(a) + parseFloat(b.total) + parseFloat(b.tax) - parseFloat(b.discount)).toFixed(2), 0);
      },
      subTotal()     { return this.sales_array.reduce((a, b) => (parseFloat(a) + parseFloat(b.total)).toFixed(2), 0); },
      discountTotal(){ return this.sales_array.reduce((a, b) => (parseFloat(a) + parseFloat(b.discount)).toFixed(2), 0); },
      taxTotal()     { return this.sales_array.reduce((a, b) => (parseFloat(a) + parseFloat(b.tax)).toFixed(2), 0); },
    },
  
    created() { this.getDebtor(); this.getCompany(); this.getActiveBusiness(); },
  
    methods: {
      paymentClass(type) {
        const map = { cash:'payment--cash', card:'payment--card', transfer:'payment--transfer', part:'payment--part', split:'payment--split', credit:'payment--credit' };
        return map[type] ?? '';
      },
  
      getActiveBusiness() {
        axios.get('/admin/business/user/active-business')
          .then(res => { this.business = res.data.data ?? {}; })
          .catch(() => {});
      },

      getDebtor() {
        this.loading = true;
        axios.get(`/debtors/id/${this.$route.params.id}`)
          .then(response => {
            this.loading      = false;
            this.debtor_debts = response.data.data;
            this.debtor       = response.data.data.debtor;
            this.sales        = this.debtor_debts?.sales;
            this.sales_items  = this.debtor_debts?.sales?.sales_item;
            this.sales_array  = this.sales_items.map(s => ({
              product_name:  s?.product?.product_name,
              selling_price: s?.selling_price,
              variant_name:  s?.variant?.variant_name ?? s?.product?.unit_of_measurement,
              quantity:      s?.quantity,
              tax:           parseFloat(s.tax_amount),
              discount:      parseFloat(s?.discount ?? 0),
              total:         parseFloat(s?.amount_paid),
            }));
            this.def_balance = (this.debtor_debts?.total_amount - this.debtor_debts?.amount_paid).toFixed(2);
            this.balance     = this.def_balance;
          })
          .catch(() => { this.loading = false; this.toast.error('Something went wrong'); });
      },
  
      handleAmount() {
        const outstanding = this.debtor_debts?.total_amount - this.debtor_debts?.amount_paid;
        this.balance      = (outstanding - this.amount).toFixed(2);
        if (Math.sign(this.balance) === -1) {
          this.amount  = this.debtor_debts?.balance;
          this.balance = parseFloat(outstanding).toFixed(2);
        }
      },
  
      makePayment() {
        if (!this.payment_type) { this.toast.error('Please select a payment type first'); return; }
        this.crudLoading = true;
        axios.post('/debtors/pay-debt', {
          amount:      this.amount,
          customer_id: this.debtor?.customer_id,
          debt_id:     this.debtor_debts?.id,
        }).then(response => {
          this.crudLoading = false;
          this.$router.push({ name: 'Debtor' });
          this.toast.success(response.data.message);
        }).catch(error => {
          this.crudLoading = false;
          this.toast.error(error.response?.data?.message?.[0] ?? 'Something went wrong');
        });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub     { margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .batch-pill   { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  
  /* ── Skeleton ── */
  .skeleton-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .skeleton       { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  .skeleton--table { height:220px; }
  .mt-4 { margin-top:16px; }
  
  /* ── Page Grid ── */
  .page-grid { display:grid; grid-template-columns:1fr 320px; gap:24px; align-items:start; }
  
  /* ── Invoice Panel ── */
  .invoice-panel { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:32px; box-shadow:0 4px 24px rgba(0,0,0,.06); }
  
  /* ── Invoice Header ── */
  .invoice-header      { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; margin-bottom:20px; }
  .invoice-title-block { display:flex; flex-direction:column; gap:8px; }
  .invoice-title       { font-size:36px; font-weight:800; color:#1a2b45; margin:0; line-height:1; }
  .invoice-batch       { font-family:monospace; font-size:14px; font-weight:600; color:#004AAD; background:#edf4ff; border:1px solid #c5d6f5; border-radius:20px; padding:3px 14px; display:inline-block; }
  .invoice-logo img    { max-width:100px; max-height:56px; object-fit:contain; }
  
  /* ── Dividers ── */
  .invoice-divider         { height:1px; background:#e4eaf2; margin:20px 0; }
  .invoice-divider--dashed { border:none; border-top:2px dashed #e4eaf2; background:transparent; }
  
  /* ── Parties ── */
  .invoice-parties    { display:flex; justify-content:space-between; gap:20px; flex-wrap:wrap; }
  .party-block        { display:flex; flex-direction:column; gap:3px; }
  .party-block--right { text-align:right; }
  .party-label        { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 4px; }
  .party-name         { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
  .party-detail       { font-size:13px; color:#4a5e78; margin:0; }
  
  /* ── Meta Grid ── */
  .sale-meta-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .sale-meta-item  { display:flex; flex-direction:column; gap:4px; }
  .sale-meta-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .sale-meta-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .mono            { font-family:monospace; }
  
  /* ── Invoice Table ── */
  .table-scroll    { overflow-x:auto; }
  .invoice-table   { width:100%; border-collapse:collapse; }
  .invoice-table thead tr { border-bottom:2px solid #e4eaf2; background:#f8fafc; }
  .invoice-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 12px; text-align:left; white-space:nowrap; }
  .invoice-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .invoice-table tbody tr:last-child { border-bottom:none; }
  .invoice-table tbody td { padding:10px 12px; vertical-align:middle; color:#1a2b45; }
  .text-right { text-align:right; }
  .text-muted { color:#4a5e78; }
  .fw-bold    { font-weight:700; }
  
  /* ── Totals ── */
  .totals-block   { display:flex; flex-direction:column; align-items:flex-end; margin-top:20px; }
  .totals-row     { display:flex; justify-content:space-between; width:280px; padding:6px 0; }
  .totals-label   { font-size:13px; font-weight:600; color:#7a90b0; text-transform:uppercase; }
  .totals-value   { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .totals-value--discount { color:#e03535; }
  .totals-divider { width:280px; height:1px; background:#e4eaf2; margin:6px 0; }
  .totals-row--grand .totals-label { color:#1a2b45; font-size:14px; }
  .totals-value--grand { font-size:18px; font-weight:800; color:#004AAD; }
  
  /* ── Payment ── */
  .payment-block { }
  .payment-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 14px; }
  .payment-grid  { display:flex; flex-direction:column; gap:8px; max-width:360px; }
  .payment-row   { display:flex; justify-content:space-between; align-items:center; }
  .payment-label { font-size:13px; color:#7a90b0; }
  .payment-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .text-neg      { color:#e03535; }
  
  .payment-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .payment--cash      { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .payment--card      { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .payment--transfer  { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .payment--part,
  .payment--split     { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .payment--credit    { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  
  /* ── Receipt Panel ── */
  .receipt-panel { position:sticky; top:20px; }
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Outstanding chip ── */
  .outstanding-chip { display:flex; justify-content:space-between; align-items:center; background:#fff0f0; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:4px; }
  .outstanding-label { font-size:13px; font-weight:600; color:#e03535; }
  .outstanding-value { font-size:20px; font-weight:800; color:#e03535; }
  
  /* ── Fields ── */
  .field-group  { display:flex; flex-direction:column; gap:6px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus    { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input:disabled { background:#f8fafc; color:#aab8cc; cursor:not-allowed; }
  .field-input--readonly { background:#f8fafc; }
  .field-select { appearance:auto; cursor:pointer; }
  .mt-3 { margin-top:12px; }
  .mt-4 { margin-top:16px; }
  
  /* ── Button ── */
  .btn-primary-action { display:inline-flex; align-items:center; justify-content:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:10px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .w-full { width:100%; }
  
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .page-grid { grid-template-columns:1fr; } .receipt-panel { position:static; } .sale-meta-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px)  { .invoice-panel { padding:20px; } .sale-meta-grid { grid-template-columns:1fr; } .totals-row,.totals-divider { width:100%; } .invoice-header { flex-direction:column; } }
  </style>